import i18n from '@/locales/i18n';
import { MessageItem } from '@/renderer/scripts/libs/smh-sdk-class/typings';

/**
 * @description 根据模版调整显示文本字数
 * @param contentTemplate 字符串模板
 * @param templateData 填充模板的数据
 * @param textLimit 字数限制
 */
const adjustShowContentStr = (contentTemplate: string, templateData: any, textLimit: number): string => {
  let ellipsisLimit = 3; // 允许保留的字符串长度，缩略后尽量保证有三个字符，动态调整，可以为0
  const replaceData = {
    ...templateData,
  };

  const keys = Object.keys(templateData).sort((a: string, b: string): number => {
    const aLength = templateData[a].length;
    const bLength = templateData[b].length;
    if (aLength > bLength) {
      return -1;
    }

    if (aLength < bLength) {
      return 1;
    }
    return 0;
  });

  const orgStr = contentTemplate.replace(/\$\{(\w+)\}/g, ($0: any, $1: string) => replaceData[$1] ?? '');
  do {
    let shouldEllipsisSize = orgStr.length - textLimit; // 目标
    if (textLimit === -1) {
      shouldEllipsisSize = 0;
    }
    for (let i = 0; i < keys.length; ++i) {
      replaceData[keys[i]] = templateData[keys[i]]; // 考虑到要动态调整ellipsisLimit，需要重置这个key的值
      if (shouldEllipsisSize > 0) {
        if (templateData[keys[i]].length > ellipsisLimit) {
          // 字符长度大于允许保留的长度，截断
          if (templateData[keys[i]].length - ellipsisLimit >= shouldEllipsisSize) {
            // 缩略该项可以达成目标，尽量保留多的信息
            const ellipsisText = `${(templateData[keys[i]] as string).substring(
              0,
              templateData[keys[i]].length - shouldEllipsisSize,
            )}...`;
            replaceData[keys[i]] = ellipsisText;
            shouldEllipsisSize = 0;
          } else {
            // 缩略该项不能达成目标，尽可能多缩略内容
            const ellipsisText = `${(templateData[keys[i]] as string).substring(0, ellipsisLimit)}...`;
            replaceData[keys[i]] = ellipsisText;
            shouldEllipsisSize -= templateData[keys[i]].length - ellipsisLimit;
          }
        } else if (templateData[keys[i]].length <= ellipsisLimit) {
          replaceData[keys[i]] = templateData[keys[i]];
        }
      }
    }
    if (shouldEllipsisSize <= 0) {
      // 目标完成
      break;
    }
    ellipsisLimit -= 1;
  } while (ellipsisLimit > 1);

  // 正则替换
  return contentTemplate.replace(/\$\{(\w+)\}/g, ($0: any, $1: string) => replaceData[$1] ?? '');
};

export const getMessageShowContent = (detail: MessageItem) => {
  const showLinkBtn = detail.linkName && detail.linkName.length;
  // 计算缩略后的字符串
  let textLimit = showLinkBtn ? 69 : 73;
  if (detail.internationalLinkName && i18n.locale in detail.internationalLinkName) {
    switch (i18n.locale) {
      case 'zh-CN':
        textLimit = showLinkBtn ? 69 : 73;
        break;
      default:
        textLimit = showLinkBtn ? 118 : 125;
    }
  }
  if (
    detail.internationalTemplate &&
    detail.internationalTemplateData &&
    i18n.locale in detail.internationalTemplate &&
    i18n.locale in detail.internationalTemplateData &&
    detail.internationalTemplate[i18n.locale] &&
    detail.internationalTemplateData[i18n.locale]
  ) {
    const { internationalTemplate, internationalTemplateData } = detail;
    return adjustShowContentStr(internationalTemplate[i18n.locale], internationalTemplateData[i18n.locale], textLimit);
  }
  return detail.content;
};

export const getMessageShowLinkBtn = (detail: MessageItem) => {
  // TODO 20250403 交大暂时屏蔽inside-space跳转，代码比公有云差异太多，待后期直接升级
  if (detail.linkType === 'inside-space') return '';
  if (
    detail.internationalLinkName &&
    i18n.locale in detail.internationalLinkName &&
    detail.internationalLinkName[i18n.locale]
  ) {
    return detail.internationalLinkName[i18n.locale];
  }
  return detail.linkName;
};

export const getMessageShowTitle = (detail: MessageItem) => {
  if (detail.internationalTitle && i18n.locale in detail.internationalTitle && detail.internationalTitle[i18n.locale]) {
    return detail.internationalTitle[i18n.locale];
  }
  return detail.title;
};

export const getMessageFullContent = (detail: MessageItem) => {
  if (
    detail.internationalTemplate &&
    detail.internationalTemplateData &&
    i18n.locale in detail.internationalTemplate &&
    i18n.locale in detail.internationalTemplateData &&
    detail.internationalTemplate[i18n.locale] &&
    detail.internationalTemplateData[i18n.locale]
  ) {
    const { internationalTemplate, internationalTemplateData } = detail;
    const localTemplateData = internationalTemplateData[i18n.locale];
    return internationalTemplate[i18n.locale].replace(
      /\$\{(\w+)\}/g,
      ($0: any, $1: string) => localTemplateData[$1] ?? '',
    );
  }

  return detail.content;
};
